// src/pages/Header.jsx
import * as React from 'react';
import * as PropTypes from 'prop-types';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Avatar } from '@progress/kendo-react-layout';
import { useLocalization } from '@progress/kendo-react-intl';
import { Notification, NotificationGroup } from '@progress/kendo-react-notification';
import { Fade } from '@progress/kendo-react-animation';
import { SvgIcon } from '@progress/kendo-react-common';
import { logoutIcon, menuIcon } from '@progress/kendo-svg-icons';
import { Button } from '@progress/kendo-react-buttons';
import headerBg from '../assets/header-bg.png';
import userAvatar from '../assets/user-avatar.jpg';
import logo from '../assets/exploserv-logo-200x72.png';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppContext } from '../AppContext';

// Zentrale Position für die Notifications (oben mittig)
const position = {
  topCenter: { 
    top: '25px', 
    left: '50%', 
    transform: 'translateX(-50%)', 
    alignItems: 'center' 
  }
};

// Custom CSS für Notifications
const notificationStyles = `
  .custom-notification {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .custom-notification .k-notification-content {
    display: inline-block;
    vertical-align: middle;
  }
  .custom-notification .k-notification-close {
    margin-left: auto;
  }
`;

export const Header = (props) => {
  const { onButtonClick } = props;
  const { avatar, notifications, setNotifications } = React.useContext(AppContext);
  const localizationService = useLocalization();
  const { userId, setUserId } = useAuth();
  const navigate = useNavigate();

  const imgRef = React.useRef(null);
  const hasImage = avatar && avatar.length > 0;

  React.useEffect(() => {
    if (!userId) {
      navigate('/login', { replace: true });
    }
  }, [userId, navigate]);

  const handleLogout = () => {
    setUserId(null);
    navigate('/login', { replace: true });
  };

  React.useEffect(() => {
    if (hasImage) {
      const reader = new FileReader();
      reader.onload = function (e) {
        imgRef.current.setAttribute('src', e.target.result);
      };
      reader.readAsDataURL(avatar[0].getRawFile());
    }
  }, [avatar, hasImage]);

  const handleClose = React.useCallback((id) => {
    setNotifications((current) => current.filter(item => item.id !== id));
  }, [setNotifications]);

  React.useEffect(() => {
    const timers = notifications.map(notification => {
      return setTimeout(() => {
        handleClose(notification.id);
      }, 2000);
    });

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [notifications, handleClose]);

  return (
    <header className="header" style={{ backgroundImage: `url(${headerBg})` }}>
      <style>{notificationStyles}</style>
      <div className="nav-container">
        <div className="menu-button">
          <SvgIcon icon={menuIcon} onClick={onButtonClick} />
        </div>
        <div className="title">
          <img src={logo} alt="EXPLOSERV Logo" className="appbar-logo" />
        </div>
        <div className="settings">
          {userId && (
            <Button
              type="button"
              fillMode="flat"
              svgIcon={logoutIcon}
              onClick={handleLogout}
            >
              {localizationService.toLanguageString('custom.logout', 'Abmelden')}
            </Button>
          )}
        </div>
        <Avatar type={'image'} shape={'circle'}>
          {hasImage ? (
            <img ref={imgRef} src={'#'} alt={'User Avatar'} />
          ) : (
            <img src={userAvatar} alt="user-avatar" />
          )}
        </Avatar>
      </div>
      <NotificationGroup style={position.topCenter}>
        {notifications.map(notification => (
          <Fade key={notification.id}>
            <Notification
              className="custom-notification"
              type={{ style: notification.type, icon: true }}
              closable={true}
              onClose={() => handleClose(notification.id)}
            >
              <span>{notification.message}</span>
            </Notification>
          </Fade>
        ))}
      </NotificationGroup>
    </header>
  );
};

Header.displayName = 'Header';
Header.propTypes = {
  page: PropTypes.string,
  onButtonClick: PropTypes.func
};

export default Header;